import { useMemo } from 'react';
import { useAppViewModel } from '../viewmodels/AppViewModel';
import { AppTheme } from '../types';
import { IThemeService, createThemeService } from '../services/ThemeService';

/**
 * Hook customizado para gerenciar tema da aplicação
 * Segue o princípio da Responsabilidade Única (SRP)
 */
export const useTheme = () => {
  const { themeClasses, isLoading, error } = useAppViewModel();

  // Memoização das classes de tema
  const memoizedTheme = useMemo(() => {
    return themeClasses;
  }, [themeClasses]);

  // Helpers para acessar classes específicas
  const getBackgroundClass = (variant: string = 'primary'): string => {
    return memoizedTheme?.backgrounds?.[variant] || '';
  };

  const getTextClass = (variant: string = 'primary'): string => {
    return memoizedTheme?.text?.[variant] || '';
  };

  const getBorderClass = (variant: string = 'primary'): string => {
    return memoizedTheme?.borders?.[variant] || '';
  };

  const getGradientClass = (variant: string = 'primary'): string => {
    return memoizedTheme?.gradients?.[variant] || '';
  };

  const getShadowClass = (variant: string = 'sm'): string => {
    return memoizedTheme?.shadows?.[variant] || '';
  };

  const getButtonClass = (variant: string = 'primary'): string => {
    return memoizedTheme?.components?.buttons?.[variant] || '';
  };

  const getCardClass = (): string => {
    return memoizedTheme?.components?.cards?.default || '';
  };

  const getInputClass = (): string => {
    return memoizedTheme?.components?.inputs?.default || '';
  };

  const getAnimationClass = (animation: string): string => {
    return memoizedTheme?.animations?.[animation] || '';
  };

  // Função para combinar classes
  const combineClasses = (...classes: (string | undefined | null)[]): string => {
    return classes.filter(Boolean).join(' ');
  };

  // Função para aplicar tema responsivo
  const getResponsiveClass = (baseClass: string, breakpoint?: string): string => {
    if (!breakpoint || !memoizedTheme?.responsive?.[breakpoint]) {
      return baseClass;
    }
    return `${baseClass} ${breakpoint}:${baseClass}`;
  };

  return {
    theme: memoizedTheme,
    isLoading,
    error,
    // Helpers para classes específicas
    getBackgroundClass,
    getTextClass,
    getBorderClass,
    getGradientClass,
    getShadowClass,
    getButtonClass,
    getCardClass,
    getInputClass,
    getAnimationClass,
    // Utilitários
    combineClasses,
    getResponsiveClass
  };
};

/**
 * Hook para gerenciar mudanças de tema
 */
export const useThemeManager = () => {
  const themeService: IThemeService = createThemeService();

  const updateTheme = async (newTheme: Partial<AppTheme>): Promise<void> => {
    try {
      await themeService.updateTheme(newTheme);
      // O tema será atualizado automaticamente através do AppViewModel
    } catch (error) {
      console.error('Erro ao atualizar tema:', error);
      throw error;
    }
  };

  const resetTheme = async (): Promise<void> => {
    try {
      await themeService.resetTheme();
    } catch (error) {
      console.error('Erro ao resetar tema:', error);
      throw error;
    }
  };

  return {
    updateTheme,
    resetTheme
  };
};